/**
 * Meta Pixel (browser-only) — consent-gated singleton.
 *
 * The Pixel is NEVER loaded until the visitor grants marketing/analytics
 * consent (see ConsentBanner -> s2s_consent_v1.analytics). No token/secret is
 * involved; the Pixel ID is public and read from REACT_APP_META_PIXEL_ID.
 *
 * Public helpers:
 *   initMetaPixel()      — load fbevents.js + init + first PageView (idempotent)
 *   trackEvent(name, p)  — fire a standard/custom event (no-op if not inited)
 *   trackPurchase(v, c)  — fire Purchase with value + currency
 *   revokeMetaPixel()    — stop future sends if consent is withdrawn
 */
const PIXEL_ID_ENV = process.env.REACT_APP_META_PIXEL_ID;
let PIXEL_ID = PIXEL_ID_ENV;

let initialized = false;

/** Override the Pixel ID at runtime (e.g. from the admin-configured value
 *  fetched via /api/marketing/public-config). Env stays the fallback. */
export function setPixelId(id) {
  if (id && !initialized) PIXEL_ID = String(id).trim();
}

function installFbqStub() {
  if (window.fbq) return;
  const n = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  s.parentNode.insertBefore(t, s);
}

export function initMetaPixel() {
  if (initialized) return true;
  if (!PIXEL_ID) {
    console.warn("Meta Pixel disabled: REACT_APP_META_PIXEL_ID is missing");
    return false;
  }
  installFbqStub();
  window.fbq("init", PIXEL_ID);
  // Explicitly grant consent to release fbevents' buffered events. We only
  // reach here AFTER the visitor accepted marketing cookies, so granting is
  // correct; without it fbevents buffers and never sends any /tr beacon.
  window.fbq("consent", "grant");
  window.fbq("track", "PageView");
  initialized = true;
  return true;
}

export function isMetaPixelInitialized() {
  return initialized;
}

export function trackEvent(eventName, params = {}) {
  if (!initialized || !window.fbq) return false;
  try {
    window.fbq("track", eventName, params);
    return true;
  } catch (e) {
    console.warn("Meta Pixel trackEvent failed", e);
    return false;
  }
}

export function trackPurchase(value, currency = "USD", extra = {}) {
  const v = Number(value);
  if (!Number.isFinite(v) || v < 0) return false;
  return trackEvent("Purchase", {
    ...extra,
    value: v,
    currency: String(currency || "USD").toUpperCase(),
  });
}

export function revokeMetaPixel() {
  try {
    if (window.fbq) window.fbq("consent", "revoke");
  } catch (e) {
    console.warn("Meta Pixel revoke failed", e);
  }
}
